import { useDispatch, useSelector } from "react-redux";
import { restConditionModalAC } from "store/actions/condition";
import { resetTableAndTabsAC } from "store/actions/table";
import { deleteSettingColorSchemeAC, resetSettingSchemeAC, setPrimaryColorAC } from "store/actions/tabsSettings";
import { updateTemplateSettingsAC } from "store/actions/templateSettings";
import { ItemsTypeEnum, initTemplateSettings, initTemporarySetting } from "store/reducers/viewerReducer";
import { getTableColumns, getTemplateSetting, getTemporarySetting } from "store/selectors/templateSettings.js";
import { clearColors, propertyInfo } from "utils/builder/IScheduleLogic";
import { getColumnType } from "utils/helpers";
import { ColorSidebarService } from "utils/services/colorSidebar.service";
import { FilterSettingsService } from "utils/services/filterSettings.service";
import { colorSchemeType } from "utils/types";

const createSettingsScheme = ({
  settingName,
  existingValue,
  visibilityName,
  metaData,
  type,
}) => {
  return {
    metaData: {
      name: settingName,
      visibilityName: visibilityName,
      isSidebarOpen: false,
      isPrimaryColor: false,
      isHiddenColumn: false,
      isCustomColumn: false,
      currentOpenTab: colorSchemeType.conditionalColor,
      ...metaData,
    },
    type,
    existingValue,
    settingsItems: {},
  };
};

const useSetting = () => {
  const dispatch = useDispatch()
  const templateSetting = useSelector(getTemplateSetting);
  const tableColumns = useSelector(getTableColumns);
  const temporarySetting = useSelector(getTemporarySetting);
  const currentOpenSettingName = temporarySetting.metaData?.name;
  const settingsItems = temporarySetting.settingsItems ?? {};
  const columnType = getColumnType(temporarySetting.type);

  const _updateTemplateSettings = (updateTemplateSettings) => {
    dispatch(updateTemplateSettingsAC(updateTemplateSettings))
  }

  const _updateTemporarySetting = (updateTemporarySetting) => {
    const updateData = {
      ...templateSetting,
      temporarySetting: {
        ...updateTemporarySetting,
      },
    };
    _updateTemplateSettings(updateData)
  }

  const _updateSettingsItems = (settingsItems) => {
    const updateData = {
      ...templateSetting,
      temporarySetting: {
        ...temporarySetting,
        settingsItems,
      },
    };
    _updateTemplateSettings(updateData)
  }

  const setHiddenColumnHandler = () => {
    const isHiddenColumn = !temporarySetting.metaData.isHiddenColumn;
    _updateTemporarySetting({
      ...temporarySetting,
      metaData: {
        ...temporarySetting.metaData,
        isHiddenColumn
      }
    })
  };

  const changeTabHandler = (tabId) => {
    _updateTemporarySetting({
      ...temporarySetting,
      metaData: { ...temporarySetting.metaData, currentOpenTab: tabId },
    });
  };

  const addGradientData = (gradientData) => {
    const currentGradientData = settingsItems.gradientData ?? {};
    const updatedGradientData = { ...currentGradientData, ...gradientData };

    _updateSettingsItems({ ...settingsItems, gradientData: updatedGradientData });
  };

  const addColorItemHandler = () => {
    const newColorItem = ColorSidebarService.createColorItemSetting(columnType);

    const currentSetting = settingsItems;
    const isPrimaryColor = temporarySetting.metaData.isPrimaryColor
    const currentColors = currentSetting?.[ItemsTypeEnum.colors] ?? [];
    const updatedColors = [...currentColors, newColorItem];

    const setMainColorWhenPrimaryUnset = () => {
      const isSomeColumnHasPrimaryColor = tableColumns.some((column) => column.metaData.isPrimaryColor === true)
      if (!isSomeColumnHasPrimaryColor && !isPrimaryColor) {
        return _updateTemporarySetting({
          ...temporarySetting,
          metaData: {
            ...temporarySetting.metaData,
            isPrimaryColor: !isPrimaryColor,
          },
          settingsItems: {
            ...currentSetting,
            [ItemsTypeEnum.colors]: updatedColors,
          }
        })
      }

      _updateSettingsItems({
        ...currentSetting,
        [ItemsTypeEnum.colors]: updatedColors,
      });
    }

    return setMainColorWhenPrimaryUnset()
  };

  const addFilterItemHandler = () => {
    const newFilter = FilterSettingsService.createFilterItemSetting(columnType);

    const currentSetting = settingsItems;
    const currentFilters = currentSetting?.[ItemsTypeEnum.filters] ?? [];
    const updatedFilters = [...currentFilters, newFilter];

    _updateSettingsItems({
      ...currentSetting,
      [ItemsTypeEnum.filters]: updatedFilters,
    });
  };

  const deleteItemsHandler = (id, filterType) => {
    const currentSetting = settingsItems;
    const currentItems = currentSetting?.[filterType] ?? [];
    const updatedItems = currentItems.filter((item) => {
      return item.id !== id;
    });

    _updateSettingsItems({
      ...currentSetting,
      [filterType]: updatedItems,
    });
  };

  const updateItem = (filterType, updatedData) => {
    const currentSetting = settingsItems;

    const currentSettingsValue = {
      [ItemsTypeEnum.colors]: currentSetting.colors,
      [ItemsTypeEnum.filters]: currentSetting.filters,
    };

    const updatedItems = currentSettingsValue[filterType].map((item) => {
      if (item.id === updatedData.id) {
        return { ...item, ...updatedData };
      }
      return item;
    });

    if (filterType === ItemsTypeEnum.colors) {
      _updateSettingsItems({ ...settingsItems, colors: updatedItems });
      return;
    }

    _updateSettingsItems({
      ...settingsItems,
      [ItemsTypeEnum.filters]: updatedItems,
    });
  };

  const clearItems = (types) => {
    const list = Array.isArray(types) ? types : [types];
    const next = { ...settingsItems };
    list.forEach((t) => {
      next[t] = [];
    });
    _updateSettingsItems(next);
  };

  const resetProjectStates = () => {
    dispatch(restConditionModalAC());
    dispatch(resetTableAndTabsAC())
    dispatch(resetSettingSchemeAC())
  }

  const openSideBarHandler = async (optionName) => {
    let currentSetting = temporarySetting;
    let optionData = {
      existingValue: [],
      type: 0,
    }

    optionData = await propertyInfo(optionName);

    const updatedTableColumns = tableColumns.map((column) => {
      if (column.metaData?.name === optionName) {
        const updateColumn = {
          ...column,
          existingValue: optionData?.values,
          metaData: { ...column.metaData, isSidebarOpen: !column.metaData.isSidebarOpen },
        };
        currentSetting = updateColumn;
        return updateColumn;
      }
      return {
        ...column,
        metaData: { ...column.metaData, isSidebarOpen: false },
      };
    });

    if (!currentSetting) {
      return;
    }

    _updateTemplateSettings({
      ...templateSetting,
      tableColumns: updatedTableColumns,
      temporarySetting: currentSetting,
    })
  };

  const addTableColumnHandler = async ({ optionName, visibilityName, metaData, isCustomColumn = false }) => {
    let optionData = {
      existingValue: [],
      type: 0,
    }
    if (!isCustomColumn) {
      optionData = await propertyInfo(optionName);
    }
    const newTableColumn = createSettingsScheme({
      settingName: optionName,
      visibilityName: visibilityName,
      existingValue: optionData.values,
      type: optionData.type,
      metaData,
    });

    const filteredSettings = tableColumns.filter((column) => {
      return column.metaData.name !== optionName;
    });

    if (filteredSettings.length === tableColumns.length) {
      const updateData = {
        ...templateSetting,
        tableColumns: [...filteredSettings, newTableColumn],
      };
      _updateTemplateSettings(updateData);
      return;
    }

    const updateData = {
      ...templateSetting,
      tableColumns: filteredSettings,
    };

    _updateTemplateSettings(updateData);
  };

  const updateTableColumnHandler = async ({ optionName, visibilityName, prevColumnName, metaData, isCustomColumn = false }) => {
    let optionData = {
      existingValue: [],
      type: 0,
    }
    if (!isCustomColumn) {
      optionData = await propertyInfo(optionName);
    }
    const newTableColumn = createSettingsScheme({
      settingName: optionName,
      visibilityName: visibilityName,
      existingValue: optionData.values,
      type: optionData.type,
      metaData,
    });

    const filteredSettings = tableColumns.filter((column) => {
      return column.metaData.visibilityName !== prevColumnName;
    });

    const updateData = {
      ...templateSetting,
      tableColumns: [...filteredSettings, newTableColumn],
    };

    _updateTemplateSettings(updateData);
  };

  const removeTableColumn = (columnName, currentTableId = null) => {
    const updateOpenSettings =
      temporarySetting.metaData.name === columnName
        ? initTemporarySetting
        : temporarySetting;

    const filteredColumns = tableColumns.filter(
      (column) => column.metaData.name !== columnName
    );

    if (currentTableId) {
      dispatch(deleteSettingColorSchemeAC(columnName, currentTableId))
      clearColors()
    }

    const newTemplateFilterScheme = { ...templateSetting.filtersSchemes }
    if (newTemplateFilterScheme[columnName]) {
      delete newTemplateFilterScheme[columnName]
    }

    dispatch(
      updateTemplateSettingsAC({
        ...templateSetting,
        tableColumns: filteredColumns,
        temporarySetting: updateOpenSettings,
        filtersSchemes: newTemplateFilterScheme,
      })
    );
  };

  const closeAllSettings = () => {
    let currentSetting = initTemporarySetting;

    const updatedColumns = tableColumns.map((column) => {
      if (column.metaData?.name === currentOpenSettingName) {
        const updateSetting = {
          ...column,
          metaData: { ...column.metaData, isSidebarOpen: false },
        };
        currentSetting = updateSetting;
        return updateSetting;
      }
      return {
        ...column,
        metaData: { ...column.metaData, isSidebarOpen: false },
      };
    })

    _updateTemplateSettings({
      ...templateSetting,
      temporarySetting: currentSetting,
      tableColumns: updatedColumns
    })
  }

  const resetSettings = () => {
    _updateTemplateSettings(initTemplateSettings)
  }

  const addPropertyHandler = (newProperty, propertyType, callbackRequest) => {
    const NOT_FOUND = -1

    const currentPropertyByType = templateSetting[propertyType] ?? []
    const currentPropertyIndex = currentPropertyByType.findIndex(property => property === newProperty);
    const isCategoryProperty = propertyType === "categories"
    const isParametersProperty = propertyType === "properties"
    const newPropertyAlreadyExist = currentPropertyIndex !== NOT_FOUND
    const updatePropertyByType = newPropertyAlreadyExist ? currentPropertyByType.filter((property) => property !== newProperty) : [...currentPropertyByType, newProperty]

    if (isParametersProperty) {
      addTableColumnHandler({
        optionName: newProperty,
      });
    }

    callbackRequest?.(updatePropertyByType)

    _updateTemplateSettings({
      ...templateSetting,
      [propertyType]: updatePropertyByType
    })
  }

  const removePropertyGroupHandler = (propertyType, callbackRequest) => {
    const isCategoryProperty = propertyType === "categories"
    const isParametersProperty = propertyType === "properties"

    if (isCategoryProperty) {
      callbackRequest?.()
      resetSettings()
      resetProjectStates()
      return
    }
    if (isParametersProperty) {
      resetProjectStates()
      _updateTemplateSettings({
        ...templateSetting,
        [propertyType]: [],
        temporarySetting: initTemporarySetting,
        filtersSchemes: {},
        tableColumns: [],
      })
      return
    }
    _updateTemplateSettings({
      ...templateSetting,
      [propertyType]: []
    })
  }

  const saveTemporaryColumn = (tableId) => {
    const temporaryIsPrimaryColor = temporarySetting.metaData.isPrimaryColor
    const updatedTableColumns = tableColumns.map((column) => {
      if (column.metaData.name === currentOpenSettingName) {
        return { ...column, ...temporarySetting }
      }
      const isPrimaryColor = temporaryIsPrimaryColor ? false : column.metaData.isPrimaryColor;
      return { ...column, metaData: { ...column.metaData, isPrimaryColor } }
    })
    const updateData = {
      ...templateSetting,
      tableColumns: updatedTableColumns,
    }
    _updateTemplateSettings(updateData)
    if (temporaryIsPrimaryColor) {
      dispatch(setPrimaryColorAC(currentOpenSettingName, tableId));
    }
  }

  const setPrimaryColor = () => {
    _updateTemporarySetting({
      ...temporarySetting,
      metaData: {
        ...temporarySetting.metaData,
        isPrimaryColor: !temporarySetting.metaData.isPrimaryColor,
      }
    })
  }

  return {
    temporarySetting,
    settingsItems,
    changeTabHandler,
    setHiddenColumnHandler,
    addColorItemHandler,
    addFilterItemHandler,
    deleteItemsHandler,
    clearItems,
    updateItem,
    addGradientData,
    openSideBarHandler,
    addTableColumnHandler,
    removeTableColumn,
    closeAllSettings,
    resetSettings,
    setPrimaryColor,
    saveTemporaryColumn,
    addPropertyHandler,
    resetProjectStates,
    removePropertyGroupHandler,
    updateTableColumnHandler,
  };
}

export default useSetting;
